import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/classnames'

const buttonVariants = cva(
  // Base styles
  [
    'inline-flex items-center justify-center rounded font-display font-normal',
    'transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-aqua-500',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-velvet-700',
    'disabled:pointer-events-none disabled:opacity-50',
    'whitespace-nowrap',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-sky-aqua-500 text-indigo-velvet-950',
          'hover:bg-sky-aqua-400',
          'active:bg-sky-aqua-600',
        ],
        secondary: [
          'bg-tuscan-sun-500 text-indigo-velvet-950',
          'hover:bg-tuscan-sun-400',
          'active:bg-tuscan-sun-600',
        ],
        ghost: [
          'bg-transparent text-pale-slate-100',
          'hover:bg-indigo-velvet-800',
          'active:bg-indigo-velvet-900',
        ],
        outline: [
          'border border-pale-slate-700 bg-transparent text-pale-slate-100',
          'hover:bg-indigo-velvet-800 hover:border-pale-slate-600',
          'active:bg-indigo-velvet-900',
        ],
      },
      size: {
        sm: 'min-h-[44px] px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-12 px-6 text-lg',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, isLoading, children, disabled, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {isLoading ? (
          <svg
            className="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : null}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

// Export variants for use with non-button elements (e.g., Link styled as button)
export { buttonVariants }
